/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';
import React from 'react';
import { ApprovalDegree, ApprovalOddsState } from '../../types/approvalOdds';
import { CmsProduct } from '../../types/cmsProduct';
import { PushDataLayerFn } from '../../types/pushDataLayer';
import {
  ComponentTypeEnum,
  sendProductAnalytics,
  UserActionEnum,
} from '../../utils/analytics';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { sessionAttributionTracker } from '../../utils/sessionAttributionTracking/sessionAttributionTracker';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { tr } from '../../utils/translations/tr';
import Button from '../Button';
import { CarInsuranceCtaBuilderInstance } from '../CarInsurance/CarInsuranceCtaBuilder';
import { CmsImage } from '../CmsImage';
import OpenInNew from '../icons/OpenInNew';
import { TravelInsuranceCtaType } from '../TravelInsuranceCtaBuilder';
import { translations } from './i18n';

export const RewardAsideModalFooter: React.FC<{
  product: CmsProduct;
  priceLine: React.ReactNode | null;
  ctaLink: string | undefined;
  pushDataLayer: PushDataLayerFn;
  CarInsuranceCtaComponent: CarInsuranceCtaBuilderInstance | null;
  travelInsuranceCtaComponent?: (
    travelInsuranceCtaType: TravelInsuranceCtaType,
  ) => JSX.Element | undefined;
  approvalOdds?: ApprovalDegree | null;
  approvalOddsState?: ApprovalOddsState | null;
}> = ({
  product,
  priceLine,
  ctaLink,
  pushDataLayer,
  CarInsuranceCtaComponent,
  travelInsuranceCtaComponent,
  approvalOdds,
  approvalOddsState,
}) => {
  const { product_image, product_name } = product;
  const context = useCmsClientContext();
  const staticText = getClientTranslations(translations, context);

  const sendAnalyticsForButtonApply = () => {
    sessionAttributionTracker.recordApplyClick();
    sendProductAnalytics({
      action: UserActionEnum.CLICK,
      componentTitle: 'Aside Modal/Bottom Stick CTA_Apply Now',
      componentType: ComponentTypeEnum.DRAWER,
      event: 'product_click_apply',
      index: '0',
      product: product,
      pushDataLayer,
      approvalDegree: approvalOdds,
      approvalDegreeState: approvalOddsState
    });
  };

  const productNameLineClamp = priceLine ? 'line-clamp-1' : 'line-clamp-2';

  return (
    <div className="flex items-center justify-between gap-2 border border-borders/default p-spacing/padding/x py-4 bg-fill/card">
      <div className="flex items-center gap-2">
        <div className="rounded-radius/tooltip border border-borders/default p-spacing/gap/soft-12px h-10 w-16 min-w-16 md:h-16 md:w-32 md:min-w-32">
          <CmsImage
            sizes={{ _: 64, md: 128 }}
            alt={product_name ?? 'Product image'}
            src={product_image ?? ''}
            width={64}
            height={40}
            className="size-full object-contain"
          />
        </div>
        <div className="flex-col">
          <div
            className={`alpha-title-2 text-font-size/xs leading-4 md:text-base ${productNameLineClamp} break-all`}
          >
            {product_name}
          </div>
          {priceLine}
        </div>
      </div>

      {CarInsuranceCtaComponent && (
        <CarInsuranceCtaComponent
          sendAnalytics={sendAnalyticsForButtonApply}
          product={product}
          staticText={staticText}
        />
      )}
      {travelInsuranceCtaComponent &&
        travelInsuranceCtaComponent('rewardAsideModal')}
      {ctaLink && !CarInsuranceCtaComponent && !travelInsuranceCtaComponent && (
        <div className="max-w-50">
          <a href={ctaLink}>
            <Button
              styleType="cta"
              endIcon={<OpenInNew className="size-[18px] md:size-6" />}
              onClick={() => sendAnalyticsForButtonApply()}
              className="w-full bg-brand-cta"
            >
              {tr('apply_now', staticText)}
            </Button>
          </a>
        </div>
      )}
    </div>
  );
};
